interface DataRequest {
  assignedTo: string;
  createdAt: string;
  isFlagged: boolean;
  notes: string;
  requestStatus: string;
  type: string;
  _id: string;
}

interface Project {
  projectName: string;
  dataRequests: DataRequest[];
}

export interface OrgSummaryResponse {
  organization: {
    id: string;
    name: string;
  };
  timeRange: TimeRange;
  timeGrouping: TimeGrouping;
  projectMetrics: ProjectChartResponse;
  dataRequestMetrics: ChartResponse;
  projectsNeedingAttention: Project[];
}

import { proxy } from 'valtio';
import { getOrgSummary } from '~/api/organizations2';
import type { ChartParams, ChartResponse, ProjectChartResponse, TimeGrouping, TimeRange } from '~/common/charts/types';

class OrgDashboardStore {
  loading: boolean = false;
  error: string | null = null;
  chartData: OrgSummaryResponse | null = null;
  timeRange: TimeRange = 'all';
  groupBy: TimeGrouping = 'month';

  async fetchChartData(organizationId: string, params?: Partial<ChartParams>) {
    try {
      this.loading = true;
      this.error = null;

      const response = await getOrgSummary(organizationId, {
        timeRange: params?.timeRange || this.timeRange,
        groupBy: params?.groupBy || this.groupBy,
      });

      this.chartData = response;
      // TODO: this should be at the high level of the repsonse not in projectMetrics
      this.timeRange = response.timeRange;
      this.groupBy = response.timeGrouping;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'An error occurred';
      console.error('Error fetching chart data:', error);
    } finally {
      this.loading = false;
    }
  }

  // Action to update time range and fetch new data
  async updateTimeRange(organizationId: string, timeRange: TimeRange) {
    this.timeRange = timeRange;
    await this.fetchChartData(organizationId, { timeRange });
  }

  // Action to update groupBy and fetch new data
  async updateGroupBy(organizationId: string, groupBy: TimeGrouping) {
    this.groupBy = groupBy;
    await this.fetchChartData(organizationId, { groupBy });
  }

  // Reset store state
  reset() {
    this.loading = false;
    this.error = null;
    this.chartData = null;
    this.timeRange = 'all';
    this.groupBy = 'month';
  }
}

const orgDashboardStore = proxy(new OrgDashboardStore());

export default orgDashboardStore;
